import React, { useEffect, useReducer, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Dropdown, Icon } from 'semantic-ui-react';

import { getContactName } from 'helpers';
import { customerActions as actions } from 'actions';

const CustomPropValidation = (props, propName, componentName) => {
  const currentProp = props[propName];
  const propsList = ['contact', 'customer', 'serviceLocation'];

  if (typeof currentProp !== 'boolean') {
    return new Error(
      `Invalid prop \`${propName}\` of type \`${typeof currentProp}\` supplied to \`${componentName}\`, expected \`boolean\`.`,
    );
  }

  if (currentProp === true) {
    try {
      propsList.forEach(p => {
        if (propName !== p && props[p] === true) {
          throw new Error(
            `Prop \`${propName}\` cannot be \`true\` while prop \`${p}\` is \`true\`.`,
          );
        }
      });
    } catch (error) {
      return new Error(error);
    }
  }
};

const CustomerDropdown = props => {
  const [failMissing, setFailMissing] = useState(false);
  const [state, setState] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      data: [],
      loading: false,
    },
  );
  const previousParentId = useRef();

  useEffect(() => {
    if (!props.disabled) {
      getData();
    }
  }, []);

  useEffect(
    () => {
      getData(
        previousParentId.current &&
        props.parentId.id &&
        previousParentId.current !== props.parentId.id,
      );
    },
    [props.value, props.parentId.id],
  );

  useEffect(() => {
    previousParentId.current = props.parentId.id;
  });

  const getMultiple = (searchQuery = null) =>
    props.contact
      ? props.loadMultiple(
        {
          [props.parentId.type]: props.parentId.id,
        },
        searchQuery,
      )
      : props.serviceLocation
        ? props.loadMultiple(props.parentId.id, true, searchQuery)
        : props.loadMultiple(searchQuery, true);

  const getData = async (runParentCallback = false) => {
    setState({ loading: true });

    var isCustomerDDL = !props.contact && !props.serviceLocation;
    let currentValue = props.value;
    var data = [];

    if (!isCustomerDDL && !props.isPaymentTab) {
      var response = await getMultiple();
      data = response.results;
    }

    if (runParentCallback && props.parentChangeCallback) {
      currentValue = await props.parentChangeCallback(data);
    }

    const missing = await getMissing(data, currentValue);

    if (missing) {
      if (props.contact) {
        if (props.parentId.id === missing.customerId) {
          data.unshift(missing);
        } else {
          setFailMissing(true);
        }
      } else {
        data.unshift(missing);
      }
    }

    setState({data, loading: false});

    return data;
  };

  const handleChange = (e, data) =>
    props.onChange(e, {
      ...data,
      value: data.value,
    });

  const getMissing = async (data, currentValue) =>
    currentValue &&
      data.filter(
        x =>
          x[
          props.contact
            ? 'contactId'
            : props.serviceLocation
              ? 'serviceLocationId'
              : 'customerId'
          ] === currentValue,
      ).length === 0
      ? props.serviceLocation
        ? await props.loadSingle(props.parentId.id, currentValue, true)
        : await props.loadSingle(currentValue, true)
      : null;

  const handleSearchChange = async (e, { searchQuery }) => {
    const response = await getMultiple(searchQuery);
    const data = response.results;

    if (searchQuery === '') {
      const missing = await getMissing(data);

      if (missing) {
        data.unshift(missing);
      }
    }

    setState({ data });
  };

  const nameFunction = x => {
    const name = props.contact
      ? getContactName(x, props.dictionaryTerm.value)
      : props.serviceLocation
        ? `${x.name}${props.readOnly ? '' : x.fullAddress ? `, ${x.fullAddress}` : ''
        }`
        : x.name;
    return {
      text: name ? name.replace(/(\s?ID_(\d)+)/, '') : '',
      value: props.contact
        ? x.contactId || 0
        : props.serviceLocation
          ? x.serviceLocationId || x.customerId
          : x.customerId,
    };
  };

  const options = state.data ? state.data.map(x => nameFunction(x)) : [];

  if (props.showAddButton) {
    options.unshift({
      text: (
        <span style={{ color: '#428bca' }}>
          <Icon name="add circle" /> Add New {props.dictionaryTerm.value}
        </span>
      ),
      value: 'add',
    });
  }

  const selected =
    options.length > 0
      ? failMissing && props.contact && state.data.length >= 2
        ? options.find(o => o.value === state.data[1].contactId)
        : options.find(o => o.value === props.value)
      : null;

  const className = `customerInfo-${props.contact ? 'contact' : props.serviceLocation ? 'SL' : 'customer'
    }`;

  return (
    <>
      {!props.hideLabel && <label>{props.dictionaryTerm.value}</label>}

      {props.readOnly ? (
        <p style={{ fontSize: 16, marginBottom: 0, marginTop: 5 }}>
          {state.loading ? (
            <Icon loading name="spinner" />
          ) : selected ? (
            selected.text
          ) : (
            <em>None</em>
          )}
        </p>
      ) : (
        <Dropdown
          className={className}
          clearable={props.clearable}
          disabled={props.disabled}
          fluid={props.fluid}
          icon={props.icon}
          id={props.id}
          loading={state.loading}
          noResultsMessage={`No ${props.dictionaryTerm.pluralValue
            } to display.`}
          onBlur={(e, { options }) => {
            if (options.length === 0) {
              getData();
            }
          }}
          onChange={handleChange}
          onSearchChange={handleSearchChange}
          options={options}
          placeholder={props.dictionaryTerm.value}
          search
          selection
          selectOnBlur={false}
          selectOnNavigation={false}
          value={props.value}
        />
      )}
    </>
  );
};

CustomerDropdown.defaultProps = {
  clearable: false,
  contact: false,
  customer: false,
  disabled: false,
  fluid: true,
  hideLabel: false,
  icon: null,
  parentChangeCallback: null,
  parentId: {
    id: null,
    type: null,
  },
  readOnly: false,
  serviceLocation: false,
  showAddButton: false,
  value: '',
};

CustomerDropdown.propTypes = {
  clearable: PropTypes.bool,
  contact: CustomPropValidation,
  customer: CustomPropValidation,
  dictionaryTerm: PropTypes.shape({
    pluralValue: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
  disabled: PropTypes.bool,
  fluid: PropTypes.bool,
  hideLabel: PropTypes.bool,
  icon: PropTypes.string,
  parentId: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    type: PropTypes.string,
  }),
  readOnly: PropTypes.bool,
  isPaymentTab: PropTypes.bool,
  serviceLocation: CustomPropValidation,
  showAddButton: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  // functions
  loadMultiple: PropTypes.func.isRequired,
  loadSingle: PropTypes.func.isRequired,
  parentChangeCallback: PropTypes.func,
  onChange: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) => ({
  dictionaryTerm:
    state.portal.dictionary[
    ownProps.contact
      ? 'CustomerContact'
      : ownProps.serviceLocation
        ? 'ServiceLocation'
        : 'CustomerName'
    ],
});

const mapDispatchToProps = (dispatch, ownProps) =>
  bindActionCreators(
    {
      loadMultiple: ownProps.contact
        ? actions.loadContacts
        : ownProps.serviceLocation
          ? actions.loadServiceLocations
          : actions.loadCustomers,
      loadSingle: ownProps.contact
        ? actions.loadContact
        : ownProps.serviceLocation
          ? actions.loadServiceLocation
          : actions.loadCustomer,
    },
    dispatch,
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CustomerDropdown);



// WEBPACK FOOTER //
// ./src/components/Common/Form/CustomerDropdown.js